import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "../../i18n/TranslationProvider";

import doubleVision from "../../images/double-vision.jpg";
import wordsMoving from "../../images/words-moving.jpg";

const CategorySelector = ({
  categoryType,
  categorizedItemList,
  setCategorizedItemList,
  colors,
  currentCategoryIndex,
  selectedItems,
  setSelectedItems,
  headacheParts,
  setHeadacheParts,
  setError,
}) => {
  const { t } = useTranslation();

  const tSymptom = (category, factor) => {
    const key = `symptom.${category}.${factor.toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60)}`;
    const translated = t(key);
    return translated !== key ? translated : factor;
  };
  const currentCategory = categorizedItemList[currentCategoryIndex].category;
  const currentCategoryItems = categorizedItemList[currentCategoryIndex].items || [];
  const [other, setOther] = useState("");

  const hoverImagePaths = {
    "Double vision": doubleVision,
    "Words move when reading": wordsMoving,
  };

  const headacheLabel = "Headaches caused by close work: computers, reading, gaming";
  const headacheOptions = [
    { label: t("medicalistelector.headache_location.fore"), value: "Fore" },
    { label: t("medicalistelector.headache_location.backtop"), value: "BackTop" },
    { label: t("medicalistelector.headache_location.right"), value: "Right" },
    { label: t("medicalistelector.headache_location.left"), value: "Left" },
    { label: t("medicalistelector.headache_location.behind"), value: "Behind" },
    { label: t("medicalistelector.headache_location.eyes"), value: "Eyes" },
    { label: t("medicalistelector.headache_location.backbottom"), value: "BackBottom" },
    { label: t("medicalistelector.headache_location.all"), value: "All" },
  ];

  const toggleHeadacheParts = (value) => {
    setHeadacheParts((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleToggle = (category, item) => {
    if (item === headacheLabel && selectedItems[category]?.includes(item)) {
      setHeadacheParts([]);
    }
    setSelectedItems((prev) => {
      const copy = { ...prev };
      const currentList = copy[category] ?? [];
      copy[category] = currentList.includes(item)
        ? currentList.filter((i) => i !== item)
        : [...currentList, item];
      return copy;
    });
  };

  const handleAddOther = () => {
    const item = other.trim();
    if (!item) return;

    const alreadyExists = categorizedItemList
      .find((group) => group.category === currentCategory)
      ?.items.some((existing) => existing.toLowerCase() === item.toLowerCase());

    if (alreadyExists) {
      setError("Item already exists in the list.");
      return;
    }

    setCategorizedItemList((prev) =>
      prev.map((group) =>
        group.category === currentCategory
          ? { ...group, items: [...group.items, item] }
          : group
      )
    );
    handleToggle(currentCategory, item);
    setOther("");
  };

  const availableRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    setOther("");
    if (availableRef.current) availableRef.current.scrollTop = 0;
  }, [currentCategoryIndex]);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollTop = selectedRef.current.scrollHeight;
    }
  }, [selectedItems]);

  return (
    <div className="w-full">
      <div className="flex">
        {categorizedItemList.map(({ category }) => (
          <div
            key={category}
            className="flex-1 mx-auto py-3 text-center first:rounded-tl-lg last:rounded-tr-lg"
            style={{ backgroundColor: colors[category] }}
          >
            <h6
              className="text-xs md:text-sm uppercase m-0"
              style={{ color: currentCategory === category ? "white" : "black" }}
            >
              {t(`addsymptoms.category.${category}`) !== `addsymptoms.category.${category}`
                ? t(`addsymptoms.category.${category}`)
                : category}
            </h6>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:h-96">
        <div
          className="border-[1px] border-[var(--primary)] md:rounded-bl-lg bg-white px-10 py-4 w-full md:w-1/2 overflow-y-auto"
          ref={availableRef}
        >
          <div className="flex flex-col">
            <h6 className="capitalize">
              {t(`addsymptoms.category.${currentCategory}`) !== `addsymptoms.category.${currentCategory}`
                ? t(`addsymptoms.category.${currentCategory}`)
                : currentCategory}:
            </h6>
            <p className="italic text-sm">({t("medicalistelector.choose_all")})</p>

            {currentCategoryItems.map((item) => {
              const imagePath = hoverImagePaths[item];
              return (
                <label className="group" key={item} style={{ "--checkbox-color": colors[currentCategory] }}>
                  <div className="relative flex items-start mb-2 space-x-2 cursor-pointer leading-tight">
                    <input
                      type="checkbox"
                      className="hidden peer"
                      checked={selectedItems[currentCategory]?.includes(item) ?? false}
                      onChange={() => handleToggle(currentCategory, item)}
                    />
                    <div
                      className="w-4 h-4 mt-[0.2rem] mr-0.5 shrink-0 rounded-full border-2 border-current peer-checked:bg-current"
                      style={{ color: "var(--checkbox-color)" }}
                    />
                    <span>{tSymptom(currentCategory, item)}</span>
                    {imagePath && (
                      <img
                        src={imagePath}
                        alt={item}
                        className="pointer-events-none z-10 absolute top-0 left-10 w-60 h-60 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    )}
                  </div>

                  {item === headacheLabel && selectedItems[currentCategory]?.includes(headacheLabel) && (
                    <div className="mt-3 ml-2 text-sm">
                      <h6>{t("medicalistelector.headache_location.label")}</h6>
                      {headacheOptions.map(({ label, value }) => (
                        <label key={value} className="flex items-center space-x-2">
                          <input type="checkbox" onChange={() => toggleHeadacheParts(value)} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </label>
              );
            })}

            <div className="flex items-center gap-2.5">
              <h6 className="font-bold mt-2 whitespace-nowrap">{t("medicalistelector.other")}:</h6>
              <input
                className="border-b border-black py-0.5 focus:outline-none focus:ring-0"
                type="text"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddOther(); }}
              />
            </div>
          </div>
        </div>

        <div
          className="border-[1px] border-[var(--primary)] rounded-b-lg md:rounded-br-lg bg-white px-10 py-4 w-full md:w-1/2 min-h-96 overflow-y-auto"
          ref={selectedRef}
        >
          {Object.entries(selectedItems).flatMap(([category, items]) =>
            items.map((item) => (
              <div key={item} style={{ "--checkbox-color": colors[category] }}>
                <div className="flex items-start mb-2">
                  <div
                    className="w-4 h-4 mt-[0.2rem] shrink-0 rounded-full border-2 border-current bg-current"
                    style={{ color: "var(--checkbox-color)" }}
                  />
                  <span className="ml-2">{tSymptom(category, item)}</span>
                </div>
                {item === headacheLabel && headacheParts.length > 0 && (
                  <ul className="flex flex-col list-disc text-sm ml-10 mt-2">
                    {headacheParts.map((part) => (
                      <li key={part}>{t(`medicalistelector.headache_location.${part.toLowerCase()}`)}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CategorySelector;